import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { CognitoUser } from "amazon-cognito-identity-js";
import React, { useState } from "react";
import { signIn } from "./auth";
import { MfaSetup } from "./mfa-setup";
import { MfaVerify } from "./mfa-verify";
import { NewPassword } from "./new-password";

interface LoginProps {
  onLoginSuccess: () => void;
}

type AuthState =
  | { type: "LOGIN" }
  | { type: "NEW_PASSWORD"; cognitoUser: CognitoUser }
  | { type: "MFA_SETUP"; cognitoUser: CognitoUser; secretCode: string }
  | { type: "MFA_VERIFY"; cognitoUser: CognitoUser };

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [authState, setAuthState] = useState<AuthState>({ type: "LOGIN" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await signIn(email, password);

      switch (result.type) {
        case "NEW_PASSWORD_REQUIRED":
          setAuthState({
            type: "NEW_PASSWORD",
            cognitoUser: result.cognitoUser,
          });
          break;
        case "MFA_SETUP_REQUIRED":
          setAuthState({
            type: "MFA_SETUP",
            cognitoUser: result.cognitoUser,
            secretCode: result.secretCode,
          });
          break;
        case "MFA_TOTP_REQUIRED":
          setAuthState({ type: "MFA_VERIFY", cognitoUser: result.cognitoUser });
          break;
        case "SUCCESS":
          onLoginSuccess();
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSet = (result?: {
    type: string;
    cognitoUser?: CognitoUser;
    secretCode?: string;
  }) => {
    if (
      result?.type === "MFA_SETUP_REQUIRED" &&
      result.cognitoUser &&
      result.secretCode
    ) {
      setAuthState({
        type: "MFA_SETUP",
        cognitoUser: result.cognitoUser,
        secretCode: result.secretCode,
      });
    } else {
      setAuthState({ type: "LOGIN" });
      onLoginSuccess();
    }
  };

  const handleCancel = () => {
    setAuthState({ type: "LOGIN" });
    setPassword("");
  };

  // Show MFA setup if needed
  if (authState.type === "MFA_SETUP") {
    return (
      <MfaSetup
        cognitoUser={authState.cognitoUser}
        secretCode={authState.secretCode}
        email={email}
        onSuccess={onLoginSuccess}
        onError={(err) => setError(err)}
      />
    );
  }

  // Show MFA verification if needed
  if (authState.type === "MFA_VERIFY") {
    return (
      <MfaVerify
        cognitoUser={authState.cognitoUser}
        email={email}
        onSuccess={onLoginSuccess}
        onCancel={handleCancel}
      />
    );
  }

  if (authState.type === "NEW_PASSWORD") {
    return (
      <NewPassword
        cognitoUser={authState.cognitoUser}
        onPasswordSet={handlePasswordSet}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Bayescase Admin
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Internal Analytics & App Management
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
